import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  PlanGenerationAttemptOut,
  PlanGenerationStepOut,
} from "@providers/hop-ord-server/api";
import { formatDurationRange, toLocaleDateTimeString } from "@utils";

export interface PlanGenerationStepsModalProps {
  open: boolean;
  onClose: () => void;
  attempt: PlanGenerationAttemptOut | null;
}

/**
 * Duration for this step: from this step's started-at to the next step's started-at.
 * For the last step, end is the plan attempt's `finishedAt` when present.
 */
function getStepDurationLabel(
  row: PlanGenerationAttemptOut,
  steps: PlanGenerationStepOut[],
  index: number,
): string {
  const step = steps[index];
  if (!step?.steppedAt) return "—";

  const endIso =
    index < steps.length - 1
      ? (steps[index + 1]?.steppedAt ?? null)
      : (row.finishedAt ?? null);

  if (!endIso) return "—";

  const formatted = formatDurationRange(step.steppedAt, endIso);
  return formatted !== "" ? formatted : "—";
}

const PlanGenerationStepsModal = ({
  open,
  onClose,
  attempt,
}: PlanGenerationStepsModalProps) => {
  const steps = attempt?.planGenerationSteps ?? [];

  const title =
    attempt != null
      ? `[${attempt.id}] ${attempt.planName || "-"}`
      : "Plan generation steps";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          pr: 6,
        }}
        title={title}
      >
        {title}
      </DialogTitle>
      <DialogContent dividers>
        {!steps.length ? (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            No step data recorded for this attempt.
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={48}>#</TableCell>
                <TableCell>Step</TableCell>
                <TableCell width={200}>Started at</TableCell>
                <TableCell width={140}>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {steps.map((s, index) => (
                <TableRow key={`${s.step}-${index}`}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{s.step || "—"}</TableCell>
                  <TableCell>
                    {s.steppedAt ? toLocaleDateTimeString(s.steppedAt) : "—"}
                  </TableCell>
                  <TableCell>
                    {attempt
                      ? getStepDurationLabel(attempt, steps, index)
                      : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanGenerationStepsModal;
